import { catchError, firstValueFrom, map, of } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ApiGetResult, DataService, QueryDescriptor } from "@upupa/data";

export interface AppInfo {
    version: string;
    buildDate?: string;
}
export function join(...paths: string[]): string {
    return paths.map((s) => s.replace(/^\/|\/+$/g, "").trim()).join("/");
}

@Injectable({ providedIn: "root" })
export class AppService {
    private readonly http = inject(HttpClient);
    private readonly dataService = inject(DataService);
    private _appInfo: AppInfo | null = null;
    async appInfo(): Promise<AppInfo> {
        if (!this._appInfo) {
            this._appInfo = await this._fetchAppInfo();
        }
        return this._appInfo;
    }

    private _fetchAppInfo(): Promise<AppInfo> {
        return firstValueFrom(
            this.http
                .get<AppInfo>("/assets/app-info.json", {
                    headers: {
                        "Cache-Control": "no-cache",
                        Pragma: "no-cache", // For older browsers
                    },
                })
                .pipe(
                    catchError((e) => {
                        console.error(e);
                        return of(null as AppInfo);
                    }),
                    map((r) => r as AppInfo),
                ),
        ) as Promise<AppInfo>;
    }

    async fetchTextAsset(url: string): Promise<string> {
        return firstValueFrom(
            this.http.get(url, { responseType: "text" }).pipe(
                catchError((e) => {
                    console.error(e);
                    return of("" as string);
                }),
            ),
        );
    }

    async fetchJsonAsset<T = any>(url: string, query?: QueryDescriptor, options?: any): Promise<ApiGetResult<T>> {
        return firstValueFrom(this.dataService.get<T>(url, query, options));
    }

    fetchCaseStudyBody(slug: string): Promise<string> {
        return this.fetchTextAsset(`/assets/case-studies/${slug}/body.html`);
    }

    fetchCaseStudyCardDescription(slug: string): Promise<string> {
        return this.fetchTextAsset(`/assets/case-studies/${slug}/card-description.html`);
    }

    fetchCapabilityBody(slug: string): Promise<string> {
        return this.fetchTextAsset(`/assets/capabilities/${slug}/body.html`);
    }
}
